import { Injectable, OnDestroy } from '@angular/core';
import { ActivatedRoute, ActivatedRouteSnapshot, NavigationExtras, Params, Router } from '@angular/router';

import { Observable, of, ReplaySubject } from 'rxjs';
import { catchError, map, takeUntil } from 'rxjs/operators';

import { deserialize, serialize } from './utils/query-processing.utils';

const defaultExtras: NavigationExtras = {
  replaceUrl: false,
};

@Injectable()
export class QueryParamService<DefaultSchema extends object = {}> implements OnDestroy {
  private _destroyedSubj = new ReplaySubject<void>(1);
  constructor(private _router: Router, private _route: ActivatedRoute) {}

  ngOnDestroy(): void {
    this._destroyedSubj.next();
    this._destroyedSubj.complete();
  }

  public useQueryParams<Schema>(parser: (params: Params) => Schema): Observable<Schema> {
    return this._route.queryParams.pipe(
      takeUntil(this._destroyedSubj),
      // run custom deserializer on params since angular doesn't support nested objects or dayjs
      map(params => deserialize(params)),
      // run custom parser (zod) to handle type checking and defaults/fallbacks
      map(params => parser(params)),
      catchError(error => {
        return of({} as Schema);
      }),
    );
  }

  public async updateQueryParams<T extends object = DefaultSchema>(
    // lets infer types from class if provided (most use cases), otherwise the consumer can provide their own
    queryParams: Partial<T extends DefaultSchema ? DefaultSchema : T> | null,
    opts?: NavigationExtras,
    url?: any[],
  ): Promise<void> {
    const serialized = queryParams ? serialize(queryParams) : null;
    const navigatingToNewPage = !!url;

    const getQueryParamBehavior = (params, navigatingToNewPage) => {
      // if we're navigating to a new page, we want to replace any existing query params
      if (navigatingToNewPage) return '';

      // if we're staying on same page and have new parameters lets merge with existing ones
      if (params) return 'merge';

      // if we're staying on same page and have no new parameters lets clear all existing query params
      return '';
    };

    await this._router.navigate(url || [], {
      queryParams: serialized,
      queryParamsHandling: getQueryParamBehavior(serialized, navigatingToNewPage),
      ...defaultExtras,
      ...(opts || {}),
    });
  }

  /**
   * Synchronously retrieves and parses query parameters using the provided parser.
   */
  public getQueryParamSnapshot<T extends object = DefaultSchema>(
    snapshot: ActivatedRouteSnapshot,
    parser: (params: Params) => T extends DefaultSchema ? DefaultSchema : T,
  ): T {
    const deserializedParams = deserialize(snapshot.queryParams);
    return parser(deserializedParams) as T;
  }
}
